import { useRef } from 'react';

export interface EchoBaseline<T> {
	// The value to diff a fresh gesture against. While the last recorded write
	// still matches what the store held when it was written, that write is the
	// baseline — the store hasn't echoed it yet, so comparing against the raw
	// store would read our own pending write as a brand new change. Once the
	// store moves past that starting value, the record describes a store that
	// no longer exists, and the store itself becomes the baseline again.
	resolve: (storedNow: T) => T;
	// Call after a value has actually been sent, with what was written and the
	// stored value it was written from.
	record: (wrote: T, from: T) => void;
	// Call whenever a prior write's fate is no longer known — e.g. the artifact
	// switched, or the write is known to have failed — so the next gesture is
	// measured against the real store instead of a write that may never have
	// landed.
	reset: () => void;
}

// Distinguishes "my own write, not yet echoed by the store" from "a genuine
// new value" for a buffered-then-flushed field. One instance per field group
// (cadence, sources, ...); each needs its own equality function because a
// fresh reference from the store on every delta must never register as a
// move by itself.
export function useEchoBaseline<T>(
	same: (a: T, b: T) => boolean,
): EchoBaseline<T> {
	const record = useRef<{ wrote: T; from: T } | null>(null);
	const api = useRef<EchoBaseline<T>>({
		resolve: (storedNow) =>
			record.current && same(record.current.from, storedNow)
				? record.current.wrote
				: storedNow,
		record: (wrote, from) => {
			record.current = { wrote, from };
		},
		reset: () => {
			record.current = null;
		},
	});
	return api.current;
}
